/**
 * MINDESIGN Analytics — privacy-first event tracking via Umami Cloud.
 *
 * Setup (one-time, you do this):
 *   1. Sign up free at https://cloud.umami.is/
 *   2. Add a new website (URL: https://mindesignenterprises.com)
 *   3. Copy the "Website ID" (UUID format)
 *   4. Paste it into /app/frontend/.env as REACT_APP_UMAMI_WEBSITE_ID=...
 *   5. Restart frontend (sudo supervisorctl restart frontend)
 *
 * Until the env var is set, all calls are no-ops — site works normally,
 * just no analytics being collected.
 */

const WEBSITE_ID = process.env.REACT_APP_UMAMI_WEBSITE_ID;
const SCRIPT_URL =
  process.env.REACT_APP_UMAMI_SCRIPT_URL || 'https://cloud.umami.is/script.js';

let initialized = false;

/** Inject the Umami script if it isn't already loaded. */
export const initAnalytics = () => {
  if (initialized) return;
  if (typeof window === 'undefined') return;
  if (!WEBSITE_ID) return; // no-op until configured

  if (document.querySelector('script[data-website-id]')) {
    initialized = true;
    return;
  }

  const s = document.createElement('script');
  s.async = true;
  s.defer = true;
  s.src = SCRIPT_URL;
  s.setAttribute('data-website-id', WEBSITE_ID);
  document.head.appendChild(s);
  initialized = true;
};

/**
 * Fire a custom event. Silently no-ops if Umami isn't loaded.
 * @param {string} name   e.g., "share_clicked"
 * @param {object} props  optional metadata, e.g., { section: "radio" }
 */
export const track = (name, props) => {
  if (typeof window === 'undefined') return;
  try {
    if (window.umami && typeof window.umami.track === 'function') {
      if (props && Object.keys(props).length > 0) {
        window.umami.track(name, props);
      } else {
        window.umami.track(name);
      }
    }
  } catch (err) {
    // Never break the app over analytics. Surface in dev so we can debug
    // missing event names / blocked scripts; stays silent in production.
    if (typeof console !== 'undefined' && process.env.NODE_ENV !== 'production') {
      console.debug('[analytics] track failed:', err);
    }
  }
};
